import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import getBlogs, { BlogPost } from '../config/blogs';
import { Helmet } from 'react-helmet';
import { SuperSEO } from 'react-super-seo';

function Blogs() {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedCategory, setSelectedCategory] = useState<string>('All');
    const [filteredBlogs, setFilteredBlogs] = useState<BlogPost[]>([]);

    const blogs = getBlogs();
    const categories = ['All', 'Travel Guide', 'Adventure', 'Monsoon Special', 'Food Guide'];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        let results = blogs;
        if (searchTerm) {
            results = results.filter(blog =>
                blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                blog.summary.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        if (selectedCategory !== 'All') {
            results = results.filter(blog => blog.category === selectedCategory);
        }
        setFilteredBlogs(results);
    }, [searchTerm, selectedCategory]);

    return (
        <>
            <Helmet>
                <link rel="canonical" href="https://www.aapligaadi.co.in/blogs" />
            </Helmet>
            <SuperSEO
                title="Travel Blogs & Road Trip Guides | Aapli Gaadi"
                description="Explore local travel guides, road trip tips, scuba diving guides, and best spots to visit in Konkan & Maharashtra with Aapli Gaadi."
                lang="en"
                openGraph={{
                    ogTitle: "Travel Blogs & Road Trip Guides - Aapli Gaadi",
                    ogDescription: "Discover hidden gems, road trip itineraries, and adventure guides in Konkan and Maharashtra.",
                    ogUrl: "https://www.aapligaadi.co.in/blogs",
                    ogType: "website",
                    ogSiteName: "Aapli Gaadi",
                    ogLocale: "en_US"
                }}
            />

            {/* Hero Banner Section */}
            <div className="hero-wrap" style={{ backgroundImage: `url('${process.env.PUBLIC_URL}/image/bg_1.jpg')`, backgroundSize: 'cover', backgroundPosition: 'center', height: '400px' }}>
                <div className="overlay" style={{ content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, opacity: 0.5, backgroundColor: '#000000' }}></div>
                <div className="container" style={{ position: 'relative', height: '100%' }}>
                    <div className="row no-gutters slider-text justify-content-center align-items-center" style={{ height: '100%' }}>
                        <div className="col-md-9 text-center">
                            <h1 className="mb-3 text-white font-weight-bold" style={{ fontSize: '50px', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>Aapli Gaadi Travel Blogs</h1>
                            <p style={{ fontSize: '18px', color: '#f8f9fa' }}>Explore the unexplored stories, itineraries, and travel secrets of Maharashtra & Konkan Coast.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Section */}
            <section className="ftco-section bg-light">
                <div className="container">
                    {/* Filters & Search Row */}
                    <div className="row mb-5 justify-content-between align-items-center">
                        {/* Search Bar */}
                        <div className="col-lg-4 col-md-5 mb-3 mb-md-0">
                            <div className="form-group mb-0 position-relative">
                                <input
                                    type="text"
                                    className="form-control px-4"
                                    placeholder="Search blogs..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{ borderRadius: '30px', height: '45px', border: '1px solid #ddd', paddingRight: '45px' }}
                                />
                                <span className="position-absolute" style={{ right: '20px', top: '12px', color: '#888' }}>
                                    <i className="fa fa-search"></i>
                                </span>
                            </div>
                        </div>

                        {/* Category Pills */}
                        <div className="col-lg-8 col-md-7 d-flex flex-wrap justify-content-md-end">
                            {categories.map((category) => (
                                <button
                                    key={category}
                                    className={`btn py-2 px-3 m-1 ${selectedCategory === category ? 'btn-primary' : 'btn-white'}`}
                                    onClick={() => setSelectedCategory(category)}
                                    style={{
                                        borderRadius: '25px',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        boxShadow: selectedCategory === category ? 'none' : '0px 2px 5px rgba(0,0,0,0.05)',
                                        border: selectedCategory === category ? 'none' : '1px solid #eee'
                                    }}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Blog Post Cards Grid */}
                    <div className="row d-flex">
                        {filteredBlogs.length > 0 ? (
                            filteredBlogs.map((blog, idx) => (
                                <React.Fragment key={idx}>
                                    <div className="col-md-4 d-flex mb-4">
                                        <div className="blog-entry justify-content-end bg-white rounded shadow-sm w-100 d-flex flex-column" style={{ transition: 'transform 0.3s ease, box-shadow 0.3s ease', border: '1px solid #eaeaea', overflow: 'hidden' }}>
                                            {/* Image Header */}
                                            <Link
                                                to={`/blogs/${blog.slug}`}
                                                className="block-20 d-flex align-items-end"
                                                style={{
                                                    backgroundImage: `url('${process.env.PUBLIC_URL}/image/blogs/${blog.image}')`,
                                                    backgroundSize: 'cover',
                                                    backgroundPosition: 'center',
                                                    height: '240px',
                                                    position: 'relative'
                                                }}
                                            >
                                                <span
                                                    className="badge badge-primary position-absolute"
                                                    style={{ top: '15px', left: '15px', borderRadius: '15px', padding: '6px 12px', fontSize: '12px' }}
                                                >
                                                    {blog.category}
                                                </span>
                                            </Link>

                                            {/* Card Text Content */}
                                            <div className="text p-4 d-flex flex-column flex-grow-1 justify-content-between">
                                                <div>
                                                    <div className="meta mb-2" style={{ fontSize: '13px', color: '#999' }}>
                                                        <div><span className="fa fa-calendar mr-1"></span>{blog.date}</div>
                                                        <div className="ml-2"><span className="fa fa-user mr-1"></span>By {blog.author}</div>
                                                        <div className="ml-2"><span className="fa fa-clock mr-1"></span>{blog.readTime}</div>
                                                    </div>
                                                    <h3 className="heading mb-3" style={{ fontSize: '19px', fontWeight: 700, lineHeight: '1.4' }}>
                                                        <Link to={`/blogs/${blog.slug}`} className="text-dark hover-primary">{blog.title}</Link>
                                                    </h3>
                                                    <p className="text-muted" style={{ fontSize: '14px', lineHeight: '1.6', height: '70px', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                                                        {blog.summary}
                                                    </p>
                                                </div>
                                                <div className="pt-3 border-top mt-3">
                                                    <Link
                                                        to={`/blogs/${blog.slug}`}
                                                        className="btn btn-outline-primary py-2 px-3 w-100 font-weight-bold"
                                                        style={{ borderRadius: '20px', fontSize: '13px' }}
                                                    >
                                                        Read Full Article
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {idx === 1 && (
                                        <div className="col-md-4 d-flex mb-4">
                                            <div 
                                                className="blog-entry justify-content-end bg-white rounded shadow-sm w-100 d-flex flex-column" 
                                                style={{ transition: 'transform 0.3s ease, box-shadow 0.3s ease', border: '1px solid #eaeaea', overflow: 'hidden', backgroundColor: '#fff9f4' }}
                                            >
                                                <div 
                                                    className="block-20 d-flex align-items-center justify-content-center" 
                                                    style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', height: '240px', padding: '20px', position: 'relative' }}
                                                >
                                                    <span 
                                                        className="badge badge-secondary position-absolute" 
                                                        style={{ top: '15px', right: '15px', borderRadius: '15px', padding: '4px 10px', fontSize: '10px', background: '#ff9f1c', color: '#fff', border: 'none' }}
                                                    >
                                                        SPONSORED
                                                    </span>
                                                    <div className="text-center text-white">
                                                        <h4 style={{ fontWeight: 800, color: '#fff', fontSize: '20px', marginBottom: '10px' }}>Planning a Konkan Trip?</h4>
                                                        <p style={{ fontSize: '12px', color: '#cbd5e1', marginBottom: 0, padding: '0 10px' }}>Get up to 20% off beachfront premium resorts & luxury stays in Malvan & Tarkarli!</p>
                                                    </div>
                                                </div>
                                                <div className="text p-4 d-flex flex-column flex-grow-1 justify-content-between">
                                                    <div>
                                                        <h3 className="heading mb-3" style={{ fontSize: '19px', fontWeight: 700, lineHeight: '1.4' }}>
                                                            Exclusive Partner Stay Offers
                                                        </h3>
                                                        <p className="text-muted" style={{ fontSize: '14px', lineHeight: '1.6' }}>
                                                            Book handpicked premium beach properties, heritage homestays, and cottages. Make your self-drive rental trip even more comfortable.
                                                        </p>
                                                    </div>
                                                    <div className="pt-3 border-top mt-3">
                                                        <a 
                                                            href="https://www.booking.com" 
                                                            target="_blank" 
                                                            rel="noopener noreferrer" 
                                                            className="btn btn-primary py-2 px-3 w-100 font-weight-bold" 
                                                            style={{ borderRadius: '20px', fontSize: '13px' }}
                                                        >
                                                            Claim Discount Offer
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </React.Fragment>
                            ))
                        ) : (
                            <div className="col-md-12 text-center py-5">
                                <h3 className="text-muted">No blog posts found matching your criteria.</h3>
                                <p className="text-muted">Try removing search keywords or changing category filters.</p>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </>
    );
}

export default Blogs;
